'use client'

import { Card, Divider, Flex, Stack, Text } from '@mantine/core'
import '@mantine/carousel/styles.css'
import Image, { StaticImageData } from 'next/image'

import GeneralMedicineCheckout from '@/assets/images/general-medicine-checkout.png'
import GeneralMedicinePhone from '@/assets/images/general-medicine-phone.png'
import SkuClinician from '@/assets/images/sku-clinician.png'
import SkuClinician1 from '@/assets/images/sku-clinician-1.png'
import SkuClinician2 from '@/assets/images/sku-clinician-2.png'
import SkuClinician3 from '@/assets/images/sku-clinician-3.png'
import SkuClinician4 from '@/assets/images/sku-clinician-4.png'
import SkuClinician5 from '@/assets/images/sku-clinician-5.png'
import styles from './SkuWhyGeneralMedicine.module.css'

interface WhyGeneralMedicineCard {
    title: string
    description: string
    image?: StaticImageData
    images?: StaticImageData[]
    type: 'single-image' | 'multiple-images'
}

export default function SkuWhyGeneralMedicine() {
    const cards: WhyGeneralMedicineCard[] = [
        {
            title: 'All your care in one place',
            description:
                'From virtual care to specialist consults, imaging to referrals, we help you reach your health goals.',
            image: GeneralMedicinePhone,
            type: 'single-image',
        },
        {
            title: 'A care team that cares',
            description:
                'Backed by decades of expertise at top academic institutions, our doctors and clinicians go beyond to deliver the best care.',
            images: [SkuClinician, SkuClinician2, SkuClinician1, SkuClinician3, SkuClinician5, SkuClinician4],
            type: 'multiple-images',
        },
        {
            title: 'Upfront pricing',
            description: 'Know the costs upfront for all services—visits, labs, and more—with or without insurance.',
            image: GeneralMedicineCheckout,
            type: 'single-image',
        },
    ]
    return (
        <Stack gap="md" className={styles.root}>
            {renderHeader()}
            <Divider
                bd={'1px solid var(--gm-foreground-fg-outline-primary)'}
                visibleFrom="md"
            />
            {renderBody()}
        </Stack>
    )

    function renderHeader() {
        return (
            <Text
                c={{
                    base: 'var(--Foreground-fg-primary, #1F1E1D)',
                    md: 'var(--GM-Text-Black, #181818)',
                }}
                fw={{ base: 'var(--gm-weight-display-small, 450)', md: 400 }}
                fz={{ base: 36, md: 48 }}
                lh={{ base: '130%', md: '100%' }}
                lts={{ base: '-0.24px', md: '-1.08px' }}
            >
                Why General Medicine
            </Text>
        )
    }

    function renderBody() {
        return (
            <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 45, md: 70 }} justify="space-between">
                {cards.map((card: WhyGeneralMedicineCard, index: number) => renderFeatureCard(card, index))}
            </Flex>
        )
    }

    function renderFeatureCard(card: WhyGeneralMedicineCard, index: number) {
        return (
            <Stack gap={12} w="100%" key={'feature-card-' + index}>
                {card.type === 'single-image' ? renderSingleImage(card) : renderMultipleImages(card)}
                {renderCardBody(card)}
            </Stack>
        )
    }

    function renderSingleImage(card: WhyGeneralMedicineCard) {
        if (!card.image) return null
        return (
            <Card
                p={0}
                radius="lg"
                h={{ base: 200, md: 360 }}
                w="100%"
                mah={{ base: 200, md: 360 }}
                style={{ overflow: 'hidden', position: 'relative' }}
            >
                <Image
                    src={card.image}
                    alt={card.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    style={{ objectFit: 'cover' }}
                />
            </Card>
        )
    }

    function renderMultipleImages(card: WhyGeneralMedicineCard) {
        return (
            <Flex wrap="wrap" justify="space-between" w="100%" h={{ base: 320, md: 360 }} gap={0} p={0}>
                {card.images?.map((img: StaticImageData, idx: number) => (
                    <Flex
                        justify={'center'}
                        align={'center'}
                        key={idx}
                        h={{ base: 155, md: 175 }}
                        w={'32%'}
                        mb={10}
                        style={{
                            overflow: 'hidden',
                            borderRadius: 8,
                            position: 'relative',
                        }}
                    >
                        <Image
                            src={img}
                            alt={`Clinician ${idx + 1}`}
                            fill
                            sizes="(max-width: 768px) 32vw, 150px"
                            style={{ objectFit: 'contain' }}
                        />
                    </Flex>
                ))}
            </Flex>
        )
    }

    function renderCardBody(card: WhyGeneralMedicineCard) {
        return (
            <Stack gap={8}>
                <Text
                    c={'var(--GM-Text-Black, #181818)'}
                    fz={{ base: 24, md: 30 }}
                    fw={{ base: 500, md: 400 }}
                    lh={{ base: '125%', md: '120%' }}
                    lts={{ base: '-0.48px', md: '-0.72px' }}
                >
                    {card.title}
                </Text>
                <Text
                    c={'var(--GM-Text-Black, #181818)'}
                    fz={16}
                    fw={{ base: 400, md: 500 }}
                    lh={{ base: '150%', md: '140%' }}
                >
                    {card.description}
                </Text>
            </Stack>
        )
    }
}
